import { AfterViewInit, Component, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ReplaySubject } from 'rxjs';
import { takeWhile } from 'rxjs/operators';
import { IChargeNumberManagement } from '../../contract-management/IChargeNumberManagement';
import { Contract } from '../../data-services';
import { RateCeiling } from '../../data-services/rate-ceiling';
import { Process, SelectItem, Table } from '../../shared';
import { DisplayErrors } from '../../shared/errors-sidenav';
import * as fromLookups from '../../lookups';
import { IUserInfo } from '../../user-info';
import moment from 'moment';
import { LoggingService } from '../../shared/services/logging';

@Component({
    selector: 'ef-rate-ceiling-presenter',
    templateUrl: './rate-ceiling-presenter.component.html',
    styleUrls: ['./rate-ceiling-presenter.component.scss'],
    standalone: false
})
export class RateCeilingPresenterComponent
    extends DisplayErrors<RateCeiling>
    implements OnInit, OnDestroy, AfterViewInit
{
    @Input() process: Process;

    public editing: RateCeiling = {} as RateCeiling;
    clonedRateCeiling: { [i: string]: RateCeiling } = {};

    private RateCeilingSubject = new ReplaySubject<Array<RateCeiling>>(1);
    private _rateceilings: Array<RateCeiling>;
    @Input() set rateCeilings(value: Array<RateCeiling>) {
        if (value && value.length > 0) {
            value.sort((a, b) => {
                if (a.Contractdesc < b.Contractdesc) {
                    return -1;
                }
                if (a.Contractdesc > b.Contractdesc) {
                    return 1;
                }
                if (a.Chargenumber > b.Chargenumber) {
                    return 1;
                }
                if (a.Chargenumber < b.Chargenumber) {
                    return -1;
                }
            });
        }
        this._rateceilings = value;
        this.RateCeilingSubject.next(value);

        this.contractsFilterSelectItem = value.map(x => ({
            value: x.Contractlistid,
            label: x.Contractdesc
        }));
        this.contractsFilterSelectItem = this.contractsFilterSelectItem.filter(
            (v, i, a) => a.findIndex(t => t.label === v.label) === i
        );

        this.chargeNumbersFilterSelectItem = value.map(x => ({
            value: x.CNListid,
            label: x.Chargenumber
        }));
        this.chargeNumbersFilterSelectItem =
            this.chargeNumbersFilterSelectItem.filter(
                (v, i, a) => a.findIndex(t => t.label === v.label) === i
            );

        this.costCentersFilterSelectItem = value.map(x => ({
            value: x.Costcenterdesc,
            label: x.Costcenterdesc
        }));
        this.costCentersFilterSelectItem =
            this.costCentersFilterSelectItem.filter(
                (v, i, a) => a.findIndex(t => t.label === v.label) === i
            );

        this.startDatesFilterSelectItem = value.map(x => ({
            value: x.Startdate,
            label: moment(x.Startdate).format('MM/DD/YYYY')
        }));
        this.startDatesFilterSelectItem =
            this.startDatesFilterSelectItem.filter(
                (v, i, a) => a.findIndex(t => t.label === v.label) === i
            );

        this.endDatesFilterSelectItem = value.map(x => ({
            value: x.Enddate,
            label: moment(x.Enddate).format('MM/DD/YYYY')
        }));
        this.endDatesFilterSelectItem = this.endDatesFilterSelectItem.filter(
            (v, i, a) => a.findIndex(t => t.label === v.label) === i
        );
    }
    get rateCeilings(): Array<RateCeiling> {
        return this._rateceilings;
    }

    private ContractsSubject = new ReplaySubject<Array<Contract.Contract>>(1);
    private _contracts: Array<Contract.Contract>;
    @Input() set contracts(value: Array<Contract.Contract>) {
        this._contracts = value;
        this.ContractsSubject.next(value);

        this.contractsSelectItem = value.map(x => ({
            value: x.ContractListID,
            label: x.ContractDesc
        }));
        this.contractsSelectItem = this.contractsSelectItem.filter(
            (v, i, a) => a.findIndex(t => t.label === v.label) === i
        );
    }
    get contracts(): Array<Contract.Contract> {
        return this._contracts;
    }

    private CNSubject = new ReplaySubject<Array<IChargeNumberManagement>>(1);
    private _cn: Array<IChargeNumberManagement>;
    @Input() set chargeNumbers(value: Array<IChargeNumberManagement>) {
        this._cn = value;
        this.CNSubject.next(value);

        this.chargeNumbersSelectItem = value.map(x => ({
            value: x.CNListID,
            label: x.FullName
        }));
        this.chargeNumbersSelectItem = this.chargeNumbersSelectItem.filter(
            (v, i, a) => a.findIndex(t => t.label === v.label) === i
        );
    }
    get chargeNumbers(): Array<IChargeNumberManagement> {
        return this._cn;
    }

    private CCSubject = new ReplaySubject<Array<fromLookups.IUserGroup>>(1);
    private _cc: Array<fromLookups.IUserGroup>;
    @Input() set costCenters(value: Array<fromLookups.IUserGroup>) {
        this._cc = value;
        this.CCSubject.next(value);

        this.costCentersSelectItem = value.map(x => ({
            value: x.GroupID,
            label: x.GroupName
        }));
        this.costCentersSelectItem = this.costCentersSelectItem.filter(
            (v, i, a) => a.findIndex(t => t.label === v.label) === i
        );
    }
    get costCenters(): Array<fromLookups.IUserGroup> {
        return this._cc;
    }

    @Output() AddRateCeiling = new ReplaySubject<any>(1);
    @Output() SaveRateCeiling = new ReplaySubject<any>(1);
    @Output() DeleteRateCeiling = new ReplaySubject<any>(1);
    @Output() LoadChargeNumbers = new ReplaySubject<any>(1);
    @Output() IncludeInactiveChecked = new ReplaySubject<any>(1);

    @Input() userInfo: IUserInfo;

    @ViewChild(Table, { static: true }) RateTable: Table;

    active = true;

    addFormGroup: UntypedFormGroup;

    contractsSelectItem: SelectItem[] = [];
    chargeNumbersSelectItem: SelectItem[] = [];
    costCentersSelectItem: SelectItem[] = [];

    contractsFilterSelectItem: SelectItem[] = [];
    chargeNumbersFilterSelectItem: SelectItem[] = [];
    costCentersFilterSelectItem: SelectItem[] = [];
    startDatesFilterSelectItem: SelectItem[] = [];
    endDatesFilterSelectItem: SelectItem[] = [];

    selectedContracts: Contract.Contract;
    selectedChargeNumbers: IChargeNumberManagement[] = [];

    showFR: boolean;
    showMH: boolean;
    showM2: boolean;

    moment: any;

    IsDeleteError = false;

    IsColumnFrozen = false;

    showAddDialog = false;

    includeInactive: boolean;

    constructor(private log: LoggingService) {
        super();
        this.addFormGroup = new UntypedFormGroup({
            contract: new UntypedFormControl(null),
            chargenumber: new UntypedFormControl(null),
            costcenter: new UntypedFormControl(null),
            startdate: new UntypedFormControl(null),
            enddate: new UntypedFormControl(null),
            FR: new UntypedFormControl(null),
            OH: new UntypedFormControl(null),
            MH: new UntypedFormControl(null),
            MH2: new UntypedFormControl(null),
            GA: new UntypedFormControl(null)
        });

        this.moment = moment;
    }
    ngOnDestroy() {
        this.active = false;
    }

    ngOnInit(): void {
        this.setupSubscriptions();
        if (this.userInfo) {
            this.showFR = this.userInfo.ShowFR;
            this.showMH = this.userInfo.ShowMH;
            this.showM2 = this.userInfo.ShowM2;
        }
    }

    ngAfterViewInit(): void {
        this.IsColumnFrozen = true;
    }

    setupSubscriptions() {
        this.addFormGroup.controls['contract'].valueChanges
            .pipe(takeWhile(_ => this.active))
            .subscribe(value => {
                if (value != null) {
                    this.selectedContracts = null;
                    this.LoadChargeNumbers.next(value);
                    this.selectedContracts = this.contracts.find(
                        x => x.ContractListID == value
                    );
                }
            });
        this.addFormGroup.controls['chargenumber'].valueChanges
            .pipe(takeWhile(_ => this.active))
            .subscribe(value => {
                if (value != null) {
                    this.selectedChargeNumbers = [];
                    value.forEach(el => {
                        const cn = this.chargeNumbers.find(
                            x => x.CNListID == el
                        );
                        this.selectedChargeNumbers.push(cn);
                    });
                }
            });
        this.addFormGroup.controls['costcenter'].valueChanges
            .pipe(takeWhile(_ => this.active))
            .subscribe(value => {
                if (value != null) {
                }
            });
        this.addFormGroup.controls['startdate'].valueChanges
            .pipe(takeWhile(_ => this.active))
            .subscribe(value => {
                if (value != null) {
                }
            });
        this.addFormGroup.controls['enddate'].valueChanges
            .pipe(takeWhile(_ => this.active))
            .subscribe(value => {
                if (value != null) {
                }
            });
        this.addFormGroup.controls['FR'].valueChanges
            .pipe(takeWhile(_ => this.active))
            .subscribe(value => {
                if (value != null) {
                }
            });
        this.addFormGroup.controls['OH'].valueChanges
            .pipe(takeWhile(_ => this.active))
            .subscribe(value => {
                if (value != null) {
                }
            });
        this.addFormGroup.controls['MH'].valueChanges
            .pipe(takeWhile(_ => this.active))
            .subscribe(value => {
                if (value != null) {
                }
            });
        this.addFormGroup.controls['MH2'].valueChanges
            .pipe(takeWhile(_ => this.active))
            .subscribe(value => {
                if (value != null) {
                }
            });
        this.addFormGroup.controls['GA'].valueChanges
            .pipe(takeWhile(_ => this.active))
            .subscribe(value => {
                if (value != null) {
                }
            });
    }

    onEditInit($event: { data: RateCeiling }) {
        this.editing = { ...$event.data };
    }

    onRowEditInit(rowData: RateCeiling) {
        this.clonedRateCeiling[rowData.Rateceilingid] = { ...rowData };
        this.editing = rowData;
        this.log.verbose('onRowEditInit');
    }

    onRowEditSave(rowData: RateCeiling) {
        this.SaveRateCeiling.next(rowData);
        delete this.clonedRateCeiling[rowData.Rateceilingid];
        delete this.editing;
        this.log.verbose('onRowEditSave');
    }

    onRowEditCancel(item: RateCeiling) {
        delete this.clonedRateCeiling[item.Rateceilingid];
        delete this.editing;
        this.log.verbose('onRowEditCancel');
    }

    includeInactiveChecked($event) {
        this.IncludeInactiveChecked.next($event.checked);
    }

    addRateCeiling() {
        const newRateCeiling = {
            ContractList: [this.selectedContracts],
            CNList: this.selectedChargeNumbers,
            Costcenterid: this.addFormGroup.controls['costcenter'].value,
            Startdate: this.addFormGroup.controls['startdate'].value,
            Enddate: this.addFormGroup.controls['enddate'].value,
            FRR: this.addFormGroup.controls['FR'].value,
            OHR: this.addFormGroup.controls['OH'].value,
            MHR: this.addFormGroup.controls['MH'].value,
            M2R: this.addFormGroup.controls['MH2'].value,
            GAR: this.addFormGroup.controls['GA'].value,
            Rateceilingid: null
        };
        this.AddRateCeiling.next(newRateCeiling);

        this.addFormGroup.controls['contract'].reset();
        this.addFormGroup.controls['chargenumber'].reset();
        this.addFormGroup.controls['costcenter'].reset();
        this.addFormGroup.controls['startdate'].reset();
        this.addFormGroup.controls['enddate'].reset();
        this.addFormGroup.controls['FR'].reset();
        this.addFormGroup.controls['OH'].reset();
        this.addFormGroup.controls['MH'].reset();
        this.addFormGroup.controls['MH2'].reset();
        this.addFormGroup.controls['GA'].reset();

        this.showAddDialog = false;
    }

    deleteRateCeiling(data) {
        if (data.Ceilinginuse == true) {
            alert(
                'The selected rate ceiling period contains processed activity, it cannot be deleted.'
            );
        } else {
            this.DeleteRateCeiling.next(data);
        }
    }

    filterTable($event, filterType) {
        // use this for all table filters
        if (filterType == 'contract') {
            this.RateTable.filter($event.value, 'Contractlistid', 'in');
        } else if (filterType == 'cn') {
            this.RateTable.filter($event.value, 'CNListid', 'in');
        } else if (filterType == 'cc') {
            this.RateTable.filter($event.value, 'Costcenterdesc', 'in');
        } else if (filterType == 'startdate') {
            this.RateTable.filter($event.value, 'Startdate', 'in');
        } else if (filterType == 'enddate') {
            this.RateTable.filter($event.value, 'Enddate', 'in');
        }
    }
}
